import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown } from "lucide-react";
import BotanicalBackdrop, { LeafGlyph } from "@/components/BotanicalBackdrop";
import { GENERAL_FAQS } from "@/data/faqs";
import { useProducts } from "@/context/ProductsContext";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FaqItem = ({ item, idx, open, onToggle }) => (
  <div className="hk-card overflow-hidden" data-testid={`faq-item-${idx}`}>
    <button
      onClick={onToggle}
      className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
      aria-expanded={open}
      data-testid={`faq-item-${idx}-toggle`}
    >
      <span className="serif text-[17px] sm:text-[19px] leading-snug text-[var(--hk-ink)] pr-6">{item.q}</span>
      <ChevronDown size={18} className={"text-[var(--hk-orange-deep)] transition-transform duration-300 shrink-0 " + (open ? "rotate-180" : "")} />
    </button>
    <div
      className={"grid transition-all duration-500 ease-out " + (open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}
    >
      <div className="overflow-hidden">
        <div className="px-6 pb-5 text-[14.5px] leading-[1.8] text-[var(--hk-ink-2)]">{item.a}</div>
      </div>
    </div>
  </div>
);

const FaqSection = ({ title, subtitle, items, prefix }) => {
  const [openIdx, setOpenIdx] = useState(0);
  if (!items || items.length === 0) return null;
  return (
    <section className="relative py-16">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <div className="mb-8">
          <div className="label-eyebrow">{subtitle}</div>
          <h2 className="serif mt-2 text-[26px] sm:text-[32px] text-[var(--hk-ink)] leading-tight">{title}</h2>
        </div>
        <div className="space-y-3">
          {items.map((f, i) => (
            <FaqItem
              key={`${prefix}-${i}`}
              idx={`${prefix}-${i}`}
              item={f}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const FaqPage = () => {
  usePageTitle("FAQs");
  const { allProducts } = useProducts();
  // Dynamic FAQs from the admin (with fallback to hardcoded GENERAL_FAQS)
  const [adminFaqs, setAdminFaqs] = useState(null);
  useEffect(() => {
    axios.get(`${API}/faqs`).then((r) => setAdminFaqs(r.data || [])).catch(() => setAdminFaqs([]));
  }, []);

  // Prefer admin-managed FAQs; fall back to hardcoded ones only when the DB is empty
  const globalItems = (adminFaqs && adminFaqs.length > 0)
    ? adminFaqs.filter((f) => f.is_global !== false).map((f) => ({ q: f.question, a: f.answer }))
    : GENERAL_FAQS;

  const productLinked = (slug) =>
    (adminFaqs || [])
      .filter((f) => f.linked_product === slug)
      .map((f) => ({ q: f.question, a: f.answer }));

  return (
    <>
      <section className="relative pt-16 pb-12 lg:pt-24 lg:pb-16 overflow-hidden">
        <BotanicalBackdrop variant="corners" />
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
          <LeafGlyph className="mx-auto text-[var(--hk-gold-deep)] w-6 h-6" />
          <div className="label-eyebrow mt-3">Frequently Asked</div>
          <h1 className="serif mt-4 text-[42px] sm:text-[54px] lg:text-[60px] leading-[1.05] text-[var(--hk-ink)]" data-testid="faq-page-title">
            Your questions,
            <br />
            <span className="italic-accent text-[var(--hk-orange-deep)]">honestly answered.</span>
          </h1>
        </div>
      </section>

      <FaqSection title="About HerbKranti" subtitle="General" items={globalItems} prefix="general" />

      {allProducts.map((p) => {
        const merged = [...(p.faqs || []), ...productLinked(p.slug)];
        return (
          <FaqSection
            key={p.slug}
            title={p.name}
            subtitle="Product FAQ"
            items={merged}
            prefix={p.slug}
          />
        );
      })}
    </>
  );
};

export default FaqPage;
